import {Component, effect, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {TileHistoryService} from '../../../core/services/tile-history.service';
import {ShadeToolsService} from '../../../core/services/shade-tools.service';
import {TIME_RANGE_MODE_LABELS, TimeRange, TimeRangeMode} from '../../../shared/models/time-range';
import {
  HISTORY_QUANTITY,
  HISTORY_TIMEZONE_OPTIONS,
  INTERVAL_OPTIONS_ANALOG,
  INTERVAL_OPTIONS_ANALOG_DEFAULT,
  INTERVAL_OPTIONS_BOOL_MULTISTATE,
  INTERVAL_OPTIONS_BOOL_MULTISTATE_DEFAULT,
} from '../../../shared/constants/tile-history.constants';
import {EscapeKeyService} from '../../../core/services/escape-key.service';
import {HistoryInterval, TileDataset} from '../../../shared/models/tile-dataset';
import {
  Tile,
  TILE_TYPE_SIMPLE_ANALOG,
  TILE_TYPE_SIMPLE_BOOL,
  TILE_TYPE_SIMPLE_MULTISTATE,
} from '../../../shared/models/tile';
import {Option, OptionGroup} from '../../../shared/models/option';
import {filter} from 'rxjs/operators';
import {BehaviorSubject, Subscription} from 'rxjs';
import 'chartjs-adapter-date-fns';
import {SpinnerComponent} from '../spinner/spinner.component';
import {PillComponent} from '../pill/pill.component';
import {PopoverMenuComponent} from '../popover-menu/popover-menu.component';
import {TileHistoryComponent} from '../tile-history/tile-history.component';
import {CSVConfigExporter} from '../../../features/tile-bulk-operations/shared/utils/csv-config-exporter.util';
import {TileHistoryNavigationHelper} from '../../../core/services/tile-history-navigation.helper';
import {DatetimeRangePickerComponent} from '../datetime-range-picker/datetime-range-picker.component';
import momentTimezone from 'moment-timezone/builds/moment-timezone-with-data-10-year-range';


@Component({
  selector: 'motn-tile-history-drawer',
  templateUrl: './tile-history-drawer.component.html',
  styleUrls: ['./tile-history-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SpinnerComponent,
    PopoverMenuComponent,
    TileHistoryComponent,
    PillComponent,
    DatetimeRangePickerComponent,
  ],
})
export class TileHistoryDrawerComponent implements OnInit, OnDestroy {
  private refreshTimeout: number;
  private unregisterEsc = () => {};

  private datasetsSub: Subscription;
  private intervalSub: Subscription;
  private stackingSub: Subscription;

  stackingOptions: OptionGroup;
  intervalOptions: OptionGroup;
  timeRangeOptions: OptionGroup;
  csvExportOptions: OptionGroup;
  timezoneOptions: OptionGroup;

  stackingMenuOpen: boolean = false;
  intervalMenuOpen: boolean = false;
  timeRangeMenuOpen: boolean = false;
  csvExportMenuOpen: boolean = false;
  timezoneMenuOpen: boolean = false;


  title: string = 'Data not available';
  interval: HistoryInterval = 'none';
  datasets: TileDataset[] = [];
  datasets$: BehaviorSubject<TileDataset[]> = new BehaviorSubject<TileDataset[]>(null);
  stacking: boolean = false;
  showStackingOptions: boolean;
  isLoadingView = this.tileHistoryService.drawerLoading;
  isNumericTile: boolean = false;

  datePickerOpen: boolean = false;
  datePickerStart: Date | null = null;
  datePickerEnd: Date | null = null;
  readonly maxDate = new Date();

  private get userSelectedRange(): TimeRange {
    return this.tileHistoryService.userSelectedRange();
  }

  readonly getMaxEndDate = (startDate: Date): Date => {
    const seekLength = this.tileHistoryService.getSeekLengthMs(this.getTiles());
    return new Date(startDate.getTime() + seekLength);
  };

  /**
   * Time range button text. Property (not getter) for change detection performance.
   */
  timeRangeButtonText: string = TIME_RANGE_MODE_LABELS[TimeRangeMode.Latest];

  private sampleTile: Tile;


  constructor(
    private tileHistoryService: TileHistoryService,
    private escapeKeyService: EscapeKeyService,
    private shadeService: ShadeToolsService,
  ) {
    // React to time range mode changes
    effect(() => {
      const mode = this.tileHistoryService.drawerTimeRangeMode();
      if (this.timeRangeOptions) {
        this.timeRangeOptions.changeSelected(mode);
      }
      if (mode === TimeRangeMode.Latest) {
        this.datePickerOpen = false;
      }
      // Update button text to reflect mode change
      this.updateTimeRangeButtonText();
    });

    // React to userSelectedRange changes and update button text
    // This ensures button text updates when service clears userSelectedRange during navigation
    effect(() => {
      const userRange = this.tileHistoryService.userSelectedRange();
      // Only update if we're in Custom mode (avoid unnecessary updates in Latest mode)
      if (this.tileHistoryService.drawerTimeRangeMode() === TimeRangeMode.Custom) {
        this.updateTimeRangeButtonText();
      }
    });

    effect(() => {
      this.tileHistoryService.drawerTimezone();
      this.updateTimeRangeButtonText();
    });
  }


  /**
   *
   */
  ngOnInit(): void {
    this.datasetsSub = this.tileHistoryService.drawerDatasets$.subscribe(
      (data) => {
        this.datasets = Object.values(data);

        if (this.datasets.length) {
          this.datasets$.next(this.datasets);
          this.sampleTile = this.datasets[0].tile;
          // PLAT-243: Enable controls for bool/multistate tiles too (for time range tools)
          this.isNumericTile = this.sampleTile.Type === TILE_TYPE_SIMPLE_ANALOG ||
                               this.sampleTile.Type === TILE_TYPE_SIMPLE_BOOL ||
                               this.sampleTile.Type === TILE_TYPE_SIMPLE_MULTISTATE;
        }

        // Button text will be updated reactively by the effect watching userSelectedRange
        this.updateTimeRangeButtonText();
        this.queueLoadHistory();
        this.setOptionsChoices();
        this.setTitle();
      },
    );

    this.intervalSub = this.tileHistoryService.drawerInterval$.pipe(filter(d => !!d)).subscribe(
      (data) => {
        this.interval = data;
        if (this.intervalOptions) {
          this.intervalOptions.changeSelected(data);
        }
      },
    );

    this.stackingSub = this.tileHistoryService.drawerStacking$.subscribe(
      (data) => {
        this.stacking = data;
        if (this.stackingOptions) {
          this.stackingOptions.changeSelected(this.stacking ? 'add' : 'replace');
        }
      },
    );


    this.unregisterEsc = this.escapeKeyService.register(() => this.closeChart());
    this.initializeCsvExportOptions();
    this.initializeTimezoneOptions();
  }


  ngOnDestroy() {
    this.stopRefresh();
    this.datasetsSub?.unsubscribe();
    this.unregisterEsc();
    this.intervalSub?.unsubscribe();
    this.stackingSub?.unsubscribe();
    this.datasets$.complete();
  }


  private setTitle() {
    const firstDatasetTile = this.sampleTile;

    if (firstDatasetTile) {
      const tileQty = this.getTiles().length;
      if (tileQty === 1) {
        this.title = firstDatasetTile.displayName;
      } else {
        this.title = `${tileQty} Tiles`;
      }
    }
  }


  /**
   *
   * @private
   */
  private queueLoadHistory() {
    const tiles = this.getTiles();

    this.stopRefresh();

    // Only enable auto-refresh when in "Latest" mode (PLAT-241)
    const mode = this.tileHistoryService.drawerTimeRangeMode();
    const isLatestMode = mode === TimeRangeMode.Latest;

    if (tiles.length && isLatestMode) {
      const fn = () => {
        this.tileHistoryService.fetchDrawerHistory(tiles);
      };

      let timeoutMs: number = 15000;
      if (this.interval === '12h') {
        timeoutMs = 300000; // 5 minutes
      } else if (this.interval.indexOf('h') > -1) {
        timeoutMs = 60000; // 1 minute
      }

      this.refreshTimeout = window.setTimeout(fn, timeoutMs);
    }
  }


  /**
   * Returns all the Tile objects being used by the chart currently
   */
  private getTiles(): Tile[] {
    return this.datasets.map(
      ds => ds.tile,
    );
  }


  /**
   * Prepares the options selectors for the history view
   *
   * @private
   */
  private setOptionsChoices(): void {
    if (this.intervalOptions && this.timeRangeOptions) {
      return; // choices have already been instantiated; need not go further
    }

    // Initialize time range options (available for all tile types)
    if (!this.timeRangeOptions) {
      this.initializeTimeRangeOptions();
    }

    const firstDatasetTile = this.sampleTile;

    if (firstDatasetTile) {
      let intervalOptions: Option[];
      let currentIntervalOption: Option;
      let defaultIntervalOption: HistoryInterval;

      switch (firstDatasetTile.Type) {
        case TILE_TYPE_SIMPLE_ANALOG:
          this.stackingOptions = new OptionGroup([
            {
              id: 'replace',
              name: 'Show One Tile at a Time',
              callback: () => {
                this.tileHistoryService.changeDrawerStacking(false);
              },
              selected: this.stacking === false,
            },
            {
              id: 'add',
              name: 'Stack Multiple Tiles\' Values',
              callback: () => {
                this.tileHistoryService.changeDrawerStacking(true);
              },
              selected: this.stacking === true,
            },
          ]);

          intervalOptions = INTERVAL_OPTIONS_ANALOG;

          currentIntervalOption = intervalOptions.find((o) => {
            return o.id === this.interval;
          });

          defaultIntervalOption = INTERVAL_OPTIONS_ANALOG_DEFAULT;

          break;
        case TILE_TYPE_SIMPLE_BOOL:
        case TILE_TYPE_SIMPLE_MULTISTATE:
          // PLAT-243: Only allow "Every Recorded Value" for bool/multistate tiles
          intervalOptions = INTERVAL_OPTIONS_BOOL_MULTISTATE;

          currentIntervalOption = intervalOptions.find((o) => {
            return o.id === this.interval;
          });

          defaultIntervalOption = INTERVAL_OPTIONS_BOOL_MULTISTATE_DEFAULT;

          break;
      }

      if (!currentIntervalOption) {
        this.tileHistoryService.changeDrawerInterval(defaultIntervalOption);
      }

      intervalOptions = intervalOptions.map((o) => {
        const interval = o.id as HistoryInterval;
        return this.prepareIntervalOption(interval, o.name as string);
      });

      this.intervalOptions = new OptionGroup(intervalOptions);

      if (firstDatasetTile.typeSupportsStackedTileHistory()) {
        this.showStackingOptions = true;
      } else {
        this.showStackingOptions = false;
      }
    }
  }


  closeIntervalMenu() {
    this.intervalMenuOpen = false;
  }

  closeStackingMenu() {
    this.stackingMenuOpen = false;
  }

  closeTimeRangeMenu() {
    this.timeRangeMenuOpen = false;
    // Don't close date picker here - it has its own close method
  }

  closeCsvExportMenu() {
    this.csvExportMenuOpen = false;
  }

  closeTimezoneMenu() {
    this.timezoneMenuOpen = false;
  }

  get selectedTimezone(): string {
    return this.tileHistoryService.drawerTimezone();
  }

  getTimezoneButtonText(): string {
    const tz = this.tileHistoryService.drawerTimezone();
    const parts = tz.split('/');
    return parts[parts.length - 1].replace(/_/g, ' ');
  }

  initializeTimezoneOptions(): void {
    const currentTz = this.tileHistoryService.drawerTimezone();
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const browserInList = HISTORY_TIMEZONE_OPTIONS.some(o => o.iana === browserTz);

    const allOptions = browserInList
      ? HISTORY_TIMEZONE_OPTIONS
      : [{iana: browserTz, label: `Your timezone: ${browserTz}`}, ...HISTORY_TIMEZONE_OPTIONS];

    this.timezoneOptions = new OptionGroup(
      allOptions.map(({iana, label}) => ({
        id: iana,
        name: label,
        selected: iana === currentTz,
        callback: () => {
          this.tileHistoryService.changeDrawerTimezone(iana);
          this.timezoneOptions.changeSelected(iana);
          this.closeTimezoneMenu();
        },
      })),
    );
  }


  getIntervalButtonText(): string {
    switch (this.interval) {
      case 'none':
        return 'Every Value';
      default:
        return `Interval: ${this.interval}`;
    }
  }


  getStackingButtonText(): string {
    return this.stacking ? 'Stack Tiles' : 'Single Tile';
  }


  private updateTimeRangeButtonText(): void {
    const mode = this.tileHistoryService.drawerTimeRangeMode();

    if (mode === TimeRangeMode.Latest) {
      this.timeRangeButtonText = TIME_RANGE_MODE_LABELS[TimeRangeMode.Latest];
      return;
    }

    const visibleDataPointsRange = this.getVisibleDataPointsRange();
    const userRange = this.tileHistoryService.userSelectedRange();
    const navigationRange = this.tileHistoryService.navigationRange;
    // User-selected range takes priority
    if (userRange.start && userRange.end) {
      this.timeRangeButtonText = this.formatDateRange(userRange.start, userRange.end);
      return;
    }

    // Show actual data range (what's visible on screen)
    if (visibleDataPointsRange?.start && visibleDataPointsRange?.end) {
      this.timeRangeButtonText = this.formatDateRange(visibleDataPointsRange.start, visibleDataPointsRange.end);
      return;
    }

    // Fallback: show navigation range if no data
    if (navigationRange.start && navigationRange.end) {
      this.timeRangeButtonText = this.formatDateRange(navigationRange.start, navigationRange.end);
      return;
    }

    // Default fallback
    this.timeRangeButtonText = TIME_RANGE_MODE_LABELS[TimeRangeMode.Custom];
  }

  /**
   * Gets the range of visible data points.
   */
  private getVisibleDataPointsRange(): TimeRange | null {
    const timestamps = this.datasets
      .flatMap(ds => ds.data || [])
      .map(d => new Date(d.Time).getTime())
      .filter(t => !isNaN(t));

    return timestamps.length === 0 ? null : {
      start: Math.min(...timestamps),
      end: Math.max(...timestamps)
    };
  }

  /**
   * Gets raw timestamp strings from datasets.
   * PLAT-277: Preserves microsecond precision via lexicographic comparison.
   */
  private getRawTimestampStrings(): { oldest: string | null; newest: string | null } {
    return TileHistoryNavigationHelper.extractBoundaries(this.datasets);
  }

  /**
   * Formats date range for display in the selected timezone.
   */
  private formatDateRange(start: number, end: number): string {
    const tz = this.tileHistoryService.drawerTimezone();
    const startDateOnly = momentTimezone.tz(start, tz).format('MMM D, YYYY');
    const endDateOnly = momentTimezone.tz(end, tz).format('MMM D, YYYY');
    return `${startDateOnly} - ${endDateOnly}`;
  }


  initializeTimeRangeOptions() {
    const mode = this.tileHistoryService.drawerTimeRangeMode();
    this.timeRangeOptions = new OptionGroup([
      {
        id: TimeRangeMode.Latest,
        name: TIME_RANGE_MODE_LABELS[TimeRangeMode.Latest],
        callback: () => {
          this.tileHistoryService.changeDrawerTimeRangeMode(TimeRangeMode.Latest);
          this.closeTimeRangeMenu();
        },
        selected: mode === TimeRangeMode.Latest,
      },
      {
        id: TimeRangeMode.Custom,
        name: TIME_RANGE_MODE_LABELS[TimeRangeMode.Custom],
        callback: () => {
          this.closeTimeRangeMenu();
          this.openDatePicker();
        },
        selected: mode === TimeRangeMode.Custom,
      },
    ]);
  }


  initializeCsvExportOptions() {
    this.csvExportOptions = new OptionGroup([
      {
        id: 'shown',
        name: 'Download Currently Shown Values',
        callback: () => {
          this.closeCsvExportMenu();
          this.downloadCsvForShownRange();
        },
        selected: false,
      },
      {
        id: 'full',
        name: 'Download All Values in Date Range',
        callback: () => {
          this.downloadCsvForFullRange();
        },
        selected: false,
      },
    ]);
  }

  openDatePicker(): void {
    const range = this.userSelectedRange;
    const tz = this.tileHistoryService.drawerTimezone();
    this.datePickerStart = range.start ? this.msToLocalDateInTz(range.start, tz) : null;
    this.datePickerEnd = range.end ? this.msToLocalDateInTz(range.end, tz) : null;
    this.datePickerOpen = true;
  }

  onRangeChange({start, end}: {start: Date; end: Date}): void {
    const tz = this.tileHistoryService.drawerTimezone();
    this.tileHistoryService.changeDrawerTimeRange({
      start: this.localDateToMsInTz(start, tz),
      end: this.localDateToMsInTz(end, tz, 999),
    }, true);
    this.datePickerOpen = false;
  }

  private msToLocalDateInTz(ms: number, tz: string): Date {
    const m = momentTimezone.tz(ms, tz);
    return new Date(m.year(), m.month(), m.date(), m.hours(), m.minutes(), m.seconds());
  }

  private localDateToMsInTz(date: Date, tz: string, ms = 0): number {
    return momentTimezone.tz(
      [date.getFullYear(), date.getMonth(), date.getDate(),
       date.getHours(), date.getMinutes(), date.getSeconds(), ms],
      tz,
    ).valueOf();
  }

  /**
   * Gets timestamp for navigation based on priority:
   * 1. Visible data points (when data exists - continue from actual data boundary)
   * 2. Navigation range (when no data - continue from last search window)
   * 3. Current time (for Latest mode with no data)
   * PLAT-277: Returns ISO string when available to preserve microsecond precision.
   */
  private getNavigationTimestamp(useEnd: boolean): number | string | null {
    const visibleDataPointsRange = this.getVisibleDataPointsRange();
    const navigationRange = this.tileHistoryService.navigationRange;

    // If we have visible data, navigate from the data boundary
    // This ensures we fetch adjacent data when continuing to navigate
    if (visibleDataPointsRange) {
      // Special case: single data point + navigating newer
      // Use navigation range end (where we searched to) instead of the data point
      if (useEnd && visibleDataPointsRange.start === visibleDataPointsRange.end && navigationRange.end) {
        return navigationRange.endIso || navigationRange.end;
      }
      return useEnd ? visibleDataPointsRange.end : visibleDataPointsRange.start;
    }

    // No data visible: use navigation range to continue progressing
    // This allows navigation to continue even when ranges have no data
    // PLAT-277: Prefer ISO strings to preserve microsecond precision
    if (navigationRange.start && navigationRange.end) {
      if (useEnd) {
        return navigationRange.endIso || navigationRange.end;
      } else {
        return navigationRange.startIso || navigationRange.start;
      }
    }

    // In Latest mode with no data, use current time
    const mode = this.tileHistoryService.drawerTimeRangeMode();
    if (mode === TimeRangeMode.Latest) {
      return Date.now();
    }
    return null;
  }

  /**
   * Navigate back in time. PLAT-277: Preserves timestamp precision.
   */
  navigateOlder() {
    const wasTruncatedInOlderDirection = this.datasets.some(ds =>
      ds.truncated && (ds.fetchedAscending === false || ds.fetchedAscending === null)
    );

    const { oldest, newest } = this.getRawTimestampStrings();
    const timestamp = oldest || this.getNavigationTimestamp(false);

    if (timestamp) this.tileHistoryService.navigateOlder(timestamp, wasTruncatedInOlderDirection);
  }

  /**
   * Navigate forward in time. PLAT-277: Preserves timestamp precision.
   */
  navigateNewer() {
    const wasTruncatedInNewerDirection = this.datasets.some(ds =>
      ds.truncated && ds.fetchedAscending === true
    );

    const { oldest, newest } = this.getRawTimestampStrings();
    const timestamp = newest || this.getNavigationTimestamp(true);

    if (timestamp) this.tileHistoryService.navigateNewer(timestamp, wasTruncatedInNewerDirection);
  }

  canNavigateNewer(): boolean {
    return this.tileHistoryService.drawerTimeRangeMode() === TimeRangeMode.Custom;
  }

  hasNoData(): boolean {
    return this.datasets.length === 0 || this.datasets.every(ds => !ds.data?.length);
  }

  // With interval=none, 200 raw readings from a high-frequency sensor can cluster in a narrow
  // time window, making the graph appear to show fewer than 200 points — they overlap visually.
  // The notice is still correct: 200 values were returned and more exist in the selected range.
  get showPartialRangeNotice(): boolean {
    return this.tileHistoryService.drawerTimeRangeMode() === TimeRangeMode.Custom
      && this.datasets.some(ds =>
        ds.truncated && (ds.data?.filter(d => d.Value !== null).length ?? 0) >= HISTORY_QUANTITY,
      );
  }

  readonly historyQuantity = HISTORY_QUANTITY;


  isCsvExportDisabled(): boolean {
    return this.isLoadingView() || this.datasets.length === 0 || this.datasets.length > 1 || this.hasNoData();
  }



  /**
   * Handles CSV export based on the current mode and options
   */
  exportToCsv() {
    if (this.isCsvExportDisabled()) {
      return;
    }

    const mode = this.tileHistoryService.drawerTimeRangeMode();

    if (mode === TimeRangeMode.Latest) {
      // In Latest mode, always download the shown range
      this.downloadCsvForShownRange();
    } else {
      // In Custom mode, check if user has explicitly selected a date range via date picker
      const userRange = this.userSelectedRange;

      // If no user-selected range (i.e., user navigated via Older/Newer from Latest), just download shown values
      // If user selected a range via date picker, show menu with options
      if (!userRange.start || !userRange.end) {
        // User navigated here via Older/Newer - just download what's shown
        this.downloadCsvForShownRange();
      } else {
        // User picked a custom date range - show menu with two options
        this.csvExportMenuOpen = !this.csvExportMenuOpen;
      }
    }
  }


  /**
   * Downloads CSV for currently displayed values
   */
  private downloadCsvForShownRange() {
    const visibleDataPointsRange = this.getVisibleDataPointsRange();
    if (!visibleDataPointsRange) {
      return;
    }

    this.downloadCsvFromApi(visibleDataPointsRange.start, visibleDataPointsRange.end);
  }


  /**
   * Downloads CSV for entire user-selected date range
   */
  downloadCsvForFullRange() {
    this.closeCsvExportMenu();
    if (this.userSelectedRange.start && this.userSelectedRange.end) {
      this.downloadCsvFromApi(this.userSelectedRange.start, this.userSelectedRange.end);
    }
  }


  /**
   * Fetches CSV data from the API and triggers download
   */
  private downloadCsvFromApi(rangeStart: number, rangeEnd: number) {
    if (this.datasets.length === 0) {
      return;
    }

    const tile = this.datasets[0].tile;

    this.tileHistoryService.fetchHistoryCsv$(tile.ID, this.interval, rangeStart, rangeEnd)
      .subscribe({
        next: (response) => {
          const csvData = response.body || '';

          // Generate safe filename using tile ID and timestamp
          // Format: Motion-Tile-{TileID}-Export-{UnixTimestamp}.csv
          // This matches the server's format but avoids using untrusted tile names
          const timestamp = Math.floor(Date.now() / 1000);
          const filename = `Motion-Tile-${tile.ID}-Export-${timestamp}.csv`;

          // Fire and forget - download happens asynchronously
          CSVConfigExporter.downloadCsvText(csvData, filename).subscribe({
            error: (error) => {
              console.error('Failed to download CSV:', error);
              this.shadeService.simpleOkDialog('Export Failed', 'Failed to export CSV data. Please try again.');
            }
          });
        },
        error: (err) => {
          console.error('Failed to download CSV:', err);
          this.shadeService.simpleOkDialog('Export Failed', 'Failed to export CSV data. Please try again.');
        }
      });
  }


  private prepareIntervalOption(interval: HistoryInterval, name: string): Option {
    return {
      id: interval,
      name: name,
      callback: () => {
        this.tileHistoryService.changeDrawerInterval(interval);
      },
      selected: this.interval === interval,
    };
  }



  /**
   *
   */
  private stopRefresh() {
    clearTimeout(this.refreshTimeout);
  }


  /**
   *
   */
  closeChart() {
    this.unregisterEsc();
    this.tileHistoryService.deactivate();
  }
}
